import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe, Sparkles, User, LogIn } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

export const Navigation = () => {
  const { lang, setLang, t } = useLanguage();
  const { user, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      window.location.href = `/#${id}`;
      return;
    }
    const el = document.getElementById(id) || document.getElementById("servicios");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const items = [
    { id: "lecturas", label: t("nav.readings") },
    { id: "hechiceria", label: t("nav.witchcraft") },
    { id: "consulta", label: t("nav.chat") },
    { route: "/predicciones-cumplidas", label: t("nav.predictions") },
    { id: "testimonios", label: t("nav.testimonials") },
    { id: "pago", label: t("nav.payment") },
    { id: "contacto", label: t("nav.contact") },
  ];

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-dark py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
          <Sparkles className="w-5 h-5 text-[#d4af37] group-hover:rotate-12 transition-transform" />
          <span className="font-serif text-xl tracking-wide gold-text">CrisAngel Astral</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {items.map((it) =>
            it.route ? (
              <Link
                key={it.route}
                to={it.route}
                data-testid={`nav-route-${it.route.replace(/\//g, "")}`}
                className="text-sm uppercase tracking-widest text-neutral-300 hover:text-[#d4af37] transition-colors"
              >
                {it.label}
              </Link>
            ) : (
              <button
                key={it.id}
                data-testid={`nav-${it.id}`}
                onClick={() => scrollTo(it.id)}
                className="text-sm uppercase tracking-widest text-neutral-300 hover:text-[#d4af37] transition-colors"
              >
                {it.label}
              </button>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <Link
              to={isAdmin ? "/admin" : "/mi-cuenta"}
              data-testid="nav-account"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#d4af37] hover:text-white border border-[#d4af37]/50 hover:bg-[#d4af37]/10 rounded-full px-3 py-1.5"
            >
              <User className="w-3.5 h-3.5" />
              {isAdmin ? "Admin" : (user.name?.split(" ")[0] || user.username)}
            </Link>
          ) : (
            <Link
              to="/ingresar"
              data-testid="nav-login"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-neutral-300 hover:text-[#d4af37] border border-white/10 hover:border-[#d4af37]/50 rounded-full px-3 py-1.5"
            >
              <LogIn className="w-3.5 h-3.5" />
              Ingresar
            </Link>
          )}
          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "es" ? "en" : "es")}
            className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-neutral-300 hover:text-[#d4af37] transition-colors border border-white/10 hover:border-[#d4af37]/50 rounded-full px-3 py-1.5"
          >
            <Globe className="w-3.5 h-3.5" />
            {lang === "es" ? "ES / EN" : "EN / ES"}
          </button>
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden text-neutral-200"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {open && (
        <div className="md:hidden glass-dark mt-3 mx-6 rounded-lg p-4 space-y-3" data-testid="mobile-menu">
          {items.map((it) =>
            it.route ? (
              <Link
                key={it.route}
                to={it.route}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-route-${it.route.replace(/\//g, "")}`}
                className="block w-full text-left text-sm uppercase tracking-widest text-neutral-300 hover:text-[#d4af37]"
              >
                {it.label}
              </Link>
            ) : (
              <button
                key={it.id}
                data-testid={`mobile-nav-${it.id}`}
                onClick={() => scrollTo(it.id)}
                className="block w-full text-left text-sm uppercase tracking-widest text-neutral-300 hover:text-[#d4af37]"
              >
                {it.label}
              </button>
            )
          )}
        </div>
      )}
    </header>
  );
};
